import React, { useMemo } from 'react';
import { Stage, Layer, Rect, Text, Line, Image as KonvaImage, Transformer, Circle, Group } from 'react-konva';
import Konva from 'konva';
import { LabelElement, LabelLayout } from '../../../../../types/labelDesign';

interface LabelCanvasProps {
  layout: LabelLayout;
  /** Id-ene til alle valgte elementer (0, 1 eller flere). */
  selectedIds: string[];
  /** additive=true (Shift/Ctrl-klikk) veksler medlemskap i utvalget. */
  onSelect: (id: string | null, additive?: boolean) => void;
  onElementChange: (element: LabelElement) => void;
  /** Pixel per mm (zoom-faktor). Default ca. 4 px/mm = ~100 DPI. */
  pxPerMm?: number;
  /** Lås proporsjon ved drag-skalering (Konva.Transformer keepRatio). */
  lockAspectRatio?: boolean;
  /** Snap posisjon/størrelse til rutenett ved draging/skalering. */
  snapToGrid?: boolean;
  /** Rutenett-størrelse i mm for snap. Default = GRID_MM (5). */
  gridMm?: number;
}

const GRID_MM = 5;
const GRID_COLOR = '#e5e7eb';
const BG_COLOR = '#ffffff';
const SELECT_COLOR = '#3b82f6';

export const LabelCanvas: React.FC<LabelCanvasProps> = ({
  layout,
  selectedIds,
  onSelect,
  onElementChange,
  pxPerMm = 4,
  lockAspectRatio = false,
  snapToGrid = false,
  gridMm = GRID_MM
}) => {
  const canvasWidth = layout.widthMm * pxPerMm;
  const canvasHeight = layout.heightMm * pxPerMm;

  const transformerRef = React.useRef<Konva.Transformer>(null);
  const layerRef = React.useRef<Konva.Layer>(null);

  // Snap til nærmeste rutenett-linje (i mm). No-op når snap er av.
  const snapMm = React.useCallback(
    (mm: number) => (snapToGrid ? Math.round(mm / gridMm) * gridMm : mm),
    [snapToGrid, gridMm]
  );

  // Transformer (resize-håndtak) brukes kun ved nøyaktig ÉTT valgt element —
  // uendret oppførsel fra før. Multi-select markeres med ramme i stedet.
  const primarySelectedId = selectedIds.length === 1 ? selectedIds[0] : null;

  React.useEffect(() => {
    const tr = transformerRef.current;
    const layer = layerRef.current;
    if (!tr || !layer) return;

    if (primarySelectedId) {
      const node = layer.findOne(`#${primarySelectedId}`);
      const el = layout.elements.find((e) => e.id === primarySelectedId);
      // Line bruker Konva.Line som ikke spiller godt med Transformer;
      // retning/størrelse redigeres i PropertyPanel.
      if (node && el && el.type !== 'Line') {
        tr.nodes([node]);
        tr.getLayer()?.batchDraw();
        return;
      }
    }
    tr.nodes([]);
    tr.getLayer()?.batchDraw();
  }, [primarySelectedId, layout.elements]);

  const gridLines = useMemo(() => {
    const lines: JSX.Element[] = [];
    for (let x = 0; x <= layout.widthMm; x += GRID_MM) {
      lines.push(
        <Line
          key={`v-${x}`}
          points={[x * pxPerMm, 0, x * pxPerMm, canvasHeight]}
          stroke={GRID_COLOR}
          strokeWidth={0.5}
          listening={false}
        />
      );
    }
    for (let y = 0; y <= layout.heightMm; y += GRID_MM) {
      lines.push(
        <Line
          key={`h-${y}`}
          points={[0, y * pxPerMm, canvasWidth, y * pxPerMm]}
          stroke={GRID_COLOR}
          strokeWidth={0.5}
          listening={false}
        />
      );
    }
    return lines;
  }, [layout.widthMm, layout.heightMm, pxPerMm, canvasWidth, canvasHeight]);

  const handleStageClick = (e: Konva.KonvaEventObject<MouseEvent>) => {
    if (e.target === e.target.getStage()) {
      onSelect(null);
    }
  };

  return (
    <div className="inline-block border border-[var(--color-border)] shadow-md bg-white">
      <Stage width={canvasWidth} height={canvasHeight} onClick={handleStageClick}>
        <Layer listening={false}>
          <Rect x={0} y={0} width={canvasWidth} height={canvasHeight} fill={layout.backgroundColor || BG_COLOR} />
          {gridLines}
        </Layer>
        <Layer ref={layerRef}>
          {layout.elements.map((el) => (
            <CanvasElement
              key={el.id}
              element={el}
              pxPerMm={pxPerMm}
              snapMm={snapMm}
              isSelected={selectedIds.includes(el.id)}
              onSelect={(additive) => onSelect(el.id, additive)}
              onChange={onElementChange}
            />
          ))}
          {/* Markeringsramme for multi-select (Transformer vises kun ved 1 valgt) */}
          {selectedIds.length > 1 &&
            layout.elements
              .filter((el) => selectedIds.includes(el.id))
              .map((el) => (
                <Rect
                  key={`sel-${el.id}`}
                  x={el.x * pxPerMm}
                  y={el.y * pxPerMm}
                  width={el.width * pxPerMm}
                  height={el.height * pxPerMm}
                  stroke={SELECT_COLOR}
                  strokeWidth={1}
                  dash={[4, 3]}
                  listening={false}
                />
              ))}
          {(() => {
            const sel = primarySelectedId
              ? layout.elements.find((e) => e.id === primarySelectedId)
              : null;
            return sel?.type === 'Line' ? (
              <LineResizeHandles element={sel} pxPerMm={pxPerMm} onChange={onElementChange} />
            ) : null;
          })()}
          <Transformer
            ref={transformerRef}
            rotateEnabled={false}
            keepRatio={lockAspectRatio}
            boundBoxFunc={(_oldBox, newBox) => {
              if (newBox.width < 4 || newBox.height < 4) return _oldBox;
              return newBox;
            }}
          />
        </Layer>
      </Stage>
    </div>
  );
};

interface CanvasElementProps {
  element: LabelElement;
  pxPerMm: number;
  /** Snap mm-verdi til rutenett (identitet når snap er av). */
  snapMm: (mm: number) => number;
  isSelected: boolean;
  /** additive=true ved Shift/Ctrl-klikk. */
  onSelect: (additive?: boolean) => void;
  onChange: (element: LabelElement) => void;
}

const CanvasElement: React.FC<CanvasElementProps> = ({ element, pxPerMm, snapMm, isSelected, onSelect, onChange }) => {
  const handleSelect = (e: Konva.KonvaEventObject<MouseEvent | Event>) => {
    const evt = e.evt as MouseEvent | undefined;
    const additive = !!evt && (evt.shiftKey || evt.ctrlKey || evt.metaKey);
    onSelect(additive);
  };

  const commonProps = {
    id: element.id,
    x: element.x * pxPerMm,
    y: element.y * pxPerMm,
    width: element.width * pxPerMm,
    height: element.height * pxPerMm,
    draggable: true,
    onClick: handleSelect,
    onTap: handleSelect,
    onDragEnd: (e: Konva.KonvaEventObject<DragEvent>) => {
      onChange({
        ...element,
        x: snapMm(e.target.x() / pxPerMm),
        y: snapMm(e.target.y() / pxPerMm)
      });
    },
    onTransformEnd: (e: Konva.KonvaEventObject<Event>) => {
      const node = e.target;
      const scaleX = node.scaleX();
      const scaleY = node.scaleY();
      node.scaleX(1);
      node.scaleY(1);
      onChange({
        ...element,
        x: snapMm(node.x() / pxPerMm),
        y: snapMm(node.y() / pxPerMm),
        width: Math.max(2, snapMm((node.width() * scaleX) / pxPerMm)),
        height: Math.max(2, snapMm((node.height() * scaleY) / pxPerMm))
      });
    }
  };

  switch (element.type) {
    case 'Text':
      return (
        <Text
          {...commonProps}
          text={element.text || ''}
          fontSize={(element.fontSize || 10) * (pxPerMm / 2.835)} // mm → pt approx
          fontStyle={element.italic ? (element.bold ? 'italic bold' : 'italic') : element.bold ? 'bold' : 'normal'}
          fontFamily={element.fontFamily || 'Arial'}
          fill={element.color || '#000000'}
          align={(element.textAlign || 'Left').toLowerCase() as 'left' | 'center' | 'right'}
          verticalAlign="middle"
        />
      );

    case 'Rectangle': {
      // Hollow hit-testing: uten fyllfarge er kun kanten klikkbar (med utvidet hit-stroke)
      // slik at elementer inni rektangelet fortsatt kan velges. Med fyllfarge brukes
      // standard full-flate-hit.
      const hasFill =
        !!element.fillColor && element.fillColor !== 'transparent' && element.fillColor !== 'none';
      const strokeW = (element.borderWidth || 0.3) * pxPerMm;
      return (
        <Rect
          {...commonProps}
          fill={hasFill ? element.fillColor : undefined}
          fillEnabled={hasFill}
          stroke={element.borderColor || '#000000'}
          strokeWidth={strokeW}
          hitStrokeWidth={hasFill ? undefined : Math.max(10, strokeW + 8)}
        />
      );
    }

    case 'Line': {
      // Konva.Line gir oss hit-stroke for klikking på tynne linjer, og støtter
      // vertikal orientering ved å la height > width.
      const isVertical = element.height > element.width;
      const lengthPx = (isVertical ? element.height : element.width) * pxPerMm;
      const strokeW = Math.max(1, (element.borderWidth || 0.3) * pxPerMm);
      const selStroke = isSelected ? '#3b82f6' : (element.borderColor || '#000000');
      return (
        <Line
          id={element.id}
          x={element.x * pxPerMm}
          y={element.y * pxPerMm}
          points={isVertical ? [0, 0, 0, lengthPx] : [0, 0, lengthPx, 0]}
          stroke={selStroke}
          strokeWidth={strokeW}
          hitStrokeWidth={Math.max(12, strokeW + 10)}
          draggable
          onClick={handleSelect}
          onTap={handleSelect}
          onDragEnd={(e) => {
            onChange({
              ...element,
              x: snapMm(e.target.x() / pxPerMm),
              y: snapMm(e.target.y() / pxPerMm)
            });
          }}
        />
      );
    }

    case 'Barcode':
      return (
        <BarcodePreviewElement
          element={element}
          commonProps={commonProps}
          isSelected={isSelected}
          pxPerMm={pxPerMm}
        />
      );

    case 'QrCode':
      return (
        <QrPreviewElement
          element={element}
          commonProps={commonProps}
          isSelected={isSelected}
        />
      );

    case 'Image':
      return <ImageCanvasElement element={element} commonProps={commonProps} isSelected={isSelected} />;

    default:
      return null;
  }
};

/**
 * Endepunkt-handles for Line-elementer. Renderes som siblings i Layer når en linje er valgt.
 * Dra start-handle for å flytte startpunktet (endepunktet står stille), eller end-handle for å
 * strekke linjen. Orientering (horisontal/vertikal) avgjøres av bbox (width vs height).
 */
interface LineResizeHandlesProps {
  element: LabelElement;
  pxPerMm: number;
  onChange: (element: LabelElement) => void;
}

const LineResizeHandles: React.FC<LineResizeHandlesProps> = ({ element, pxPerMm, onChange }) => {
  const isVertical = element.height > element.width;
  const startX = element.x * pxPerMm;
  const startY = element.y * pxPerMm;
  const endX = isVertical ? startX : startX + element.width * pxPerMm;
  const endY = isVertical ? startY + element.height * pxPerMm : startY;

  const commitStart = (e: Konva.KonvaEventObject<DragEvent>) => {
    const hx = e.target.x() / pxPerMm;
    const hy = e.target.y() / pxPerMm;
    if (isVertical) {
      const bottom = element.y + element.height;
      const newHeight = Math.max(1, bottom - hy);
      onChange({ ...element, x: hx, y: hy, height: newHeight });
    } else {
      const right = element.x + element.width;
      const newWidth = Math.max(1, right - hx);
      onChange({ ...element, x: hx, y: hy, width: newWidth });
    }
  };

  const commitEnd = (e: Konva.KonvaEventObject<DragEvent>) => {
    const hx = e.target.x() / pxPerMm;
    const hy = e.target.y() / pxPerMm;
    if (isVertical) {
      const newHeight = Math.max(1, hy - element.y);
      onChange({ ...element, height: newHeight });
    } else {
      const newWidth = Math.max(1, hx - element.x);
      onChange({ ...element, width: newWidth });
    }
  };

  return (
    <>
      <Circle
        x={startX}
        y={startY}
        radius={6}
        fill="white"
        stroke="#3b82f6"
        strokeWidth={2}
        draggable
        onDragEnd={commitStart}
      />
      <Circle
        x={endX}
        y={endY}
        radius={6}
        fill="white"
        stroke="#3b82f6"
        strokeWidth={2}
        draggable
        onDragEnd={commitEnd}
      />
    </>
  );
};

/**
 * Image-element. Bruker ALLTID en Rect-node (ikke KonvaImage) for at Konva.Transformer
 * skal kunne holde stabil referanse — bytte av node-type mid-drag krasjer Transformer
 * ("Cannot read properties of null (reading 'getAbsoluteTransform')").
 *
 * Når data-URI er lastet, settes bildet som fillPatternImage skalert til Rect-dimensjonene.
 * Ellers vises placeholder med stiplet kant.
 */
interface ImageCanvasElementProps {
  element: LabelElement;
  commonProps: any;
  isSelected: boolean;
}

const ImageCanvasElement: React.FC<ImageCanvasElementProps> = ({ element, commonProps, isSelected }) => {
  const src = element.imageSource && element.imageSource.startsWith('data:') ? element.imageSource : null;
  const image = useImage(src);
  const hasImage = !!(src && image);

  // Skaler bildet til å fylle hele Rect-flaten (commonProps.width/height er allerede i px)
  const patternScale = hasImage && image
    ? { x: commonProps.width / image.width, y: commonProps.height / image.height }
    : undefined;

  return (
    <Rect
      {...commonProps}
      fill={hasImage ? undefined : '#eef2ff'}
      fillPatternImage={hasImage ? image : undefined}
      fillPatternScale={patternScale}
      fillPatternRepeat="no-repeat"
      stroke={isSelected ? '#3b82f6' : (hasImage ? undefined : '#6366f1')}
      strokeWidth={isSelected ? 1 : (hasImage ? 0 : 1)}
      dash={hasImage ? undefined : [6, 3]}
    />
  );
};

/**
 * Barcode-element. Viser falske bars som fyller hele det designede rektangelet —
 * matcher backend-rendering (CustomLabelGenerator bruker ImageScaling.Resize, så
 * strekkoden gjengis i akkurat den størrelsen brukeren designer).
 *
 * Bars er deterministisk basert på element.value slik at samme verdi alltid gir
 * samme mønster (forutsigbart for designeren). Dette er IKKE en faktisk Code128-
 * koding — kun visuell representasjon for layout-arbeid.
 */
interface BarcodePreviewElementProps {
  element: LabelElement;
  commonProps: any;
  isSelected: boolean;
  pxPerMm: number;
}

const BarcodePreviewElement: React.FC<BarcodePreviewElementProps> = ({
  element,
  commonProps,
  isSelected,
  pxPerMm
}) => {
  const value = element.value || '{{ItemNo}}';
  const showText = element.showText ?? true;
  const fontSize = element.fontSize ?? 7;

  const widthPx = commonProps.width as number;
  const heightPx = commonProps.height as number;

  // Tekst-høyde matcher backend (CustomLabelGenerator): FontSize * 0.8 (pt) → mm med 1.3× linje-høyde
  const textPx = showText
    ? Math.min(fontSize * 0.8 * 0.3528 * 1.3 * pxPerMm, heightPx / 2)
    : 0;
  const barAreaPx = heightPx - textPx;

  // Generer deterministisk bar-mønster fra value (FNV-1a hash → bar-bredder 1-4 moduler)
  const bars = React.useMemo(() => generateFauxBars(value, widthPx, barAreaPx), [value, widthPx, barAreaPx]);

  return (
    <Group {...commonProps}>
      {/* Bakgrunn fanger klikk og lar Group's onClick/onDragEnd fyre — må være listening=true */}
      <Rect
        x={0}
        y={0}
        width={widthPx}
        height={heightPx}
        fill="#ffffff"
        stroke={isSelected ? '#3b82f6' : '#9ca3af'}
        strokeWidth={1}
      />
      {bars}
      {showText && value && (
        <Text
          x={0}
          y={barAreaPx}
          width={widthPx}
          height={textPx}
          text={value}
          fontSize={fontSize * 0.8 * (pxPerMm / 2.835)}
          fontFamily="Arial"
          align={(element.textAlign || 'Center').toLowerCase() as 'left' | 'center' | 'right'}
          verticalAlign="middle"
          listening={false}
        />
      )}
    </Group>
  );
};

/**
 * Genererer en deterministisk bar-sekvens for et gitt value, slik at canvas-preview
 * er stabil. Bars veksler svart/hvitt med bredde 1-4 moduler. Returnerer kun de
 * svarte rektanglene (hvit = canvas-bakgrunn).
 */
function generateFauxBars(value: string, width: number, height: number): React.ReactElement[] {
  if (!value || width <= 0 || height <= 0) return [];

  // FNV-1a hash for deterministisk seed
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  // LCG for ekspandering av seedet til en strøm av "tilfeldige" bar-bredder
  let state = hash >>> 0;
  const next = () => {
    state = (Math.imul(state, 1103515245) + 12345) >>> 0;
    return state;
  };

  const moduleWidth = Math.max(1, width / 70); // ca. 70 moduler over bredden
  const bars: React.ReactElement[] = [];
  let x = 0;
  let isBlack = true;
  let i = 0;
  while (x < width && i < 500) {
    const barWidth = ((next() & 3) + 1) * moduleWidth;
    if (isBlack) {
      bars.push(
        <Rect
          key={`bar-${i}`}
          x={x}
          y={0}
          width={Math.min(barWidth, width - x)}
          height={height}
          fill="#000000"
          listening={false}
        />
      );
    }
    x += barWidth;
    isBlack = !isBlack;
    i++;
  }
  return bars;
}

/**
 * QR-element. Viser et 11×11 sjakkbrett-aktig mønster som fyller det designede
 * rektangelet (samme som backend etter Resize-endring). Mønsteret er deterministisk
 * basert på value. IKKE en faktisk QR-koding.
 */
interface QrPreviewElementProps {
  element: LabelElement;
  commonProps: any;
  isSelected: boolean;
}

const QrPreviewElement: React.FC<QrPreviewElementProps> = ({ element, commonProps, isSelected }) => {
  const value = element.value || '{{Value}}';
  const widthPx = commonProps.width as number;
  const heightPx = commonProps.height as number;

  const cells = React.useMemo(() => generateFauxQrCells(value, widthPx, heightPx), [value, widthPx, heightPx]);

  return (
    <Group {...commonProps}>
      {/* Bakgrunn fanger klikk og lar Group's onClick/onDragEnd fyre — må være listening=true */}
      <Rect
        x={0}
        y={0}
        width={widthPx}
        height={heightPx}
        fill="#ffffff"
        stroke={isSelected ? '#3b82f6' : '#9ca3af'}
        strokeWidth={1}
      />
      {cells}
    </Group>
  );
};

function generateFauxQrCells(value: string, width: number, height: number): React.ReactElement[] {
  if (!value || width <= 0 || height <= 0) return [];

  const grid = 11; // 11×11 ruter (kvadratisk uavhengig av designet ratio)
  const cellW = width / grid;
  const cellH = height / grid;

  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }

  const cells: React.ReactElement[] = [];
  for (let row = 0; row < grid; row++) {
    for (let col = 0; col < grid; col++) {
      // Finder-patterns i tre hjørner (3×3-bokser med ramme)
      const isFinder =
        (row < 3 && col < 3) ||
        (row < 3 && col >= grid - 3) ||
        (row >= grid - 3 && col < 3);

      let dark = false;
      if (isFinder) {
        const localRow = row < 3 ? row : grid - 1 - row;
        const localCol = col < 3 ? col : grid - 1 - col;
        // Finder-mønster: ramme + senter
        dark = localRow === 0 || localRow === 2 || localCol === 0 || localCol === 2 || (localRow === 1 && localCol === 1);
        // Korriger sentrale hjørne-bokser for høyre og bunn (blir delvis feil over) — bare hvis i hjørne
        if (row < 3 && col < 3) {
          dark = row === 0 || row === 2 || col === 0 || col === 2 || (row === 1 && col === 1);
        } else if (row < 3 && col >= grid - 3) {
          const c = col - (grid - 3);
          dark = row === 0 || row === 2 || c === 0 || c === 2 || (row === 1 && c === 1);
        } else {
          const r = row - (grid - 3);
          dark = r === 0 || r === 2 || col === 0 || col === 2 || (r === 1 && col === 1);
        }
      } else {
        // Pseudo-tilfeldig fra hash + posisjon
        const bit = (hash ^ (row * 31 + col * 17)) & 1;
        dark = bit === 1;
      }

      if (dark) {
        cells.push(
          <Rect
            key={`q${row}-${col}`}
            x={col * cellW}
            y={row * cellH}
            width={cellW}
            height={cellH}
            fill="#000000"
            listening={false}
          />
        );
      }
    }
  }
  return cells;
}

/**
 * Liten hook for å laste et HTMLImageElement fra en src (data-URI eller URL).
 * Returnerer null inntil bildet er lastet (eller hvis src er null).
 */
function useImage(src: string | null): HTMLImageElement | null {
  const [image, setImage] = React.useState<HTMLImageElement | null>(null);

  React.useEffect(() => {
    if (!src) {
      setImage(null);
      return;
    }
    const img = new window.Image();
    img.onload = () => setImage(img);
    img.onerror = () => setImage(null);
    img.src = src;
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [src]);

  return image;
}
